import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api, currentPosition } from "../lib/api";
import { useApiErrorHandler } from "../lib/session";
import type { DetectResult, SearchResult, VisitState } from "../lib/types";
import { CardTile, Notice, ProgressPanel, Spinner, VisitCardTile, categoryLabel } from "../components/bits";
import { PlaceSearch } from "../components/PlaceSearch";

type Phase = "loading" | "idle" | "locating" | "detecting" | "confirming" | "starting" | "playing";

export default function Now({
  visit,
  setVisit,
  placeTypeTargets,
}: {
  visit: VisitState | null;
  setVisit: (visit: VisitState | null) => void;
  placeTypeTargets: Record<string, number>;
}) {
  const navigate = useNavigate();
  const handleError = useApiErrorHandler();
  const [phase, setPhase] = useState<Phase>(visit ? "playing" : "loading");
  const [detected, setDetected] = useState<DetectResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [unresolvable, setUnresolvable] = useState(false);
  const [manual, setManual] = useState(false);
  // Whether the candidate came from typing rather than GPS, purely so the
  // confirmation does not claim to know where you are when it does not.
  const [typed, setTyped] = useState(false);

  // With a visit there is nothing to load. Every place that sets one here also sets
  // the phase to "playing" in the same update, so this effect does not have to.
  useEffect(() => {
    if (visit) return;
    let active = true;
    api
      .currentVisit()
      .then((result) => {
        if (!active) return;
        if (result.visit) {
          setVisit(result.visit);
          setPhase("playing");
        } else {
          setPhase("idle");
        }
      })
      .catch((err) => {
        if (!active) return;
        setError(handleError(err));
        setPhase("idle");
      });
    return () => {
      active = false;
    };
  }, [visit, setVisit, handleError]);

  const detect = useCallback(async () => {
    setError(null);
    setUnresolvable(false);
    setPhase("locating");
    try {
      const position = await currentPosition();
      setPhase("detecting");
      const result = await api.detect(position.coords.latitude, position.coords.longitude);
      setDetected(result);
      setTyped(false);
      setPhase("confirming");
    } catch (err) {
      if (err instanceof ApiError && err.isUnresolvable) {
        // Not an error so much as an answer: you are not in a settlement.
        setUnresolvable(true);
        setError(err.message);
        setManual(true);
      } else {
        setError(handleError(err));
        // A geolocation failure (permission denied, no fix, timeout) is not
        // recoverable by retrying in the same spot, so offer the fallback
        // straight away rather than making it something to go looking for.
        if (!(err instanceof ApiError)) setManual(true);
      }
      setPhase("idle");
    }
  }, [handleError]);

  function pickSearched(result: SearchResult) {
    setError(null);
    setUnresolvable(false);
    // No second lookup: /api/search returns the same osm_id a GPS detection
    // would, so the typed place and the walked-into one are the same place.
    setDetected({ candidate: result, existing: result.existing });
    setTyped(true);
    setPhase("confirming");
  }

  async function start() {
    if (!detected) return;
    setPhase("starting");
    setError(null);
    try {
      setVisit(await api.startVisit(detected.candidate));
      setPhase("playing");
    } catch (err) {
      setError(handleError(err));
      setPhase("confirming");
    }
  }

  async function end() {
    if (!visit) return;
    if (
      !confirm(
        "Besuch beenden? Deine Karten bleiben hier liegen — beim nächsten Mal machst du damit weiter.",
      )
    )
      return;
    try {
      await api.endVisit(visit.visit_id);
      setVisit(null);
      setDetected(null);
      setPhase("idle");
    } catch (err) {
      setError(handleError(err));
    }
  }

  if (phase === "loading") {
    return (
      <div className="screen screen--tabbed">
        <Spinner label="Wir machen dort weiter, wo du aufgehört hast" />
      </div>
    );
  }

  // --- Playing -------------------------------------------------------------

  if (phase === "playing" && visit) {
    return (
      <div className="screen screen--tabbed">
        <ProgressPanel
          name={visit.place.name}
          earned={visit.place.earned_points}
          target={visit.place.target_points}
          baseTarget={visit.place.starting_target}
          conquered={visit.place.conquered}
          status={visit.place.status}
        />

        {visit.place.conquered ? (
          <p className="notice" style={{ marginTop: 12 }}>
            Schon erobert. Es werden weiter Karten ausgeteilt und alles, was du festhältst,
            wird aufbewahrt — aber hier gibt es keinen Fortschritt mehr zu machen.
          </p>
        ) : visit.place.category_veto ? (
          <p className="notice" style={{ marginTop: 12 }}>
            {categoryLabel(visit.place.category_veto)} ist in {visit.place.name} gestrichen
            und wird hier nicht mehr ausgeteilt.
          </p>
        ) : null}

        {error ? (
          <div style={{ marginTop: 12 }}>
            <Notice tone="error">{error}</Notice>
          </div>
        ) : null}

        {/* Pinned above the hand rather than dealt into it: it is an extra card, so
            it never costs you one of the three. It goes once it has been confirmed. */}
        {!visit.place.visited ? (
          <div style={{ marginTop: 24 }}>
            <VisitCardTile placeName={visit.place.name} onOpen={() => navigate("/besuch")} />
          </div>
        ) : null}

        <h3 className="screen-sub" style={{ margin: "24px 0 10px", fontWeight: 700, color: "var(--ink)" }}>
          Deine Hand
        </h3>
        <div className="stack">
          {visit.hand.map((card) => (
            <CardTile key={card.id} card={card} onOpen={() => navigate(`/card/${card.id}`)} />
          ))}
        </div>

        <h3 className="screen-sub" style={{ margin: "24px 0 10px", fontWeight: 700, color: "var(--ink)" }}>
          Zurückgelegt
        </h3>
        {visit.hold.length > 0 ? (
          <div className="stack">
            {visit.hold.map((card) => (
              <CardTile
                key={card.id}
                card={card}
                onOpen={() => navigate(`/card/${card.id}`)}
                badge="ZURÜCKGELEGT"
              />
            ))}
          </div>
        ) : (
          <p className="notice muted">
            Leer. Hier ist Platz für zwei Karten, die du mitnehmen willst, ohne eine neue zu
            blockieren.
          </p>
        )}

        <div style={{ marginTop: 28 }}>
          <button className="btn btn--danger btn--block" onClick={() => void end()}>
            Besuch beenden
          </button>
          <p className="tiny muted" style={{ textAlign: "center", margin: "8px 0 0" }}>
            Deine Karten bleiben in {visit.place.name} liegen, bis du wiederkommst.
          </p>
        </div>
      </div>
    );
  }

  // --- Confirming a detected place -----------------------------------------

  if (phase === "confirming" || phase === "starting") {
    const candidate = detected!.candidate;
    const existing = detected!.existing;
    return (
      <div className="screen screen--tabbed">
        <p className="screen-sub" style={{ marginBottom: 4 }}>
          {typed ? "Du hast gewählt" : "Du bist offenbar in"}
        </p>
        <h1 className="screen-title" style={{ fontSize: "2.4rem" }}>{candidate.name}</h1>
        <p className="muted" style={{ marginTop: 0 }}>{candidate.display_name}</p>

        {existing ? (
          <div className="notice" style={{ marginTop: 16 }}>
            {existing.conquered ? (
              <>Du hast {candidate.name} bereits erobert. Ein neuer Besuch ergänzt das Archiv.</>
            ) : (
              <>
                Du warst schon einmal hier: <strong>{existing.earned_points} von{" "}
                {existing.target_points}</strong> Punkten. Dieser Besuch knüpft genau dort an.
              </>
            )}
            {existing.parked_cards > 0 ? (
              <>
                {" "}
                {existing.parked_cards === 1
                  ? "Eine Karte von letztem Mal liegt noch hier."
                  : `Deine ${existing.parked_cards} Karten von letztem Mal liegen noch hier.`}
              </>
            ) : null}
          </div>
        ) : (
          <div className="notice" style={{ marginTop: 16 }}>
            Neuland. {candidate.name} beginnt bei 0 von {placeTypeTargets[candidate.place_type] ?? placeTypeTargets.village ?? 60} Punkten.
          </div>
        )}

        {error ? (
          <div style={{ marginTop: 12 }}>
            <Notice tone="error">{error}</Notice>
          </div>
        ) : null}

        <div className="stack" style={{ marginTop: 24 }}>
          <button className="btn btn--lg btn--block" onClick={() => void start()} disabled={phase === "starting"}>
            {phase === "starting" ? <span className="spin" aria-hidden /> : null}
            {existing && !existing.conquered ? "Hier weitermachen" : `In ${candidate.name} starten`}
          </button>
          <button
            className="btn btn--quiet btn--block"
            onClick={() => {
              setDetected(null);
              setError(null);
              setPhase("idle");
            }}
            disabled={phase === "starting"}
          >
            Nicht hier — noch einmal suchen
          </button>
        </div>
      </div>
    );
  }

  // --- Idle ----------------------------------------------------------------

  const busy = phase === "locating" || phase === "detecting";
  return (
    <div className="screen screen--tabbed">
      <h1 className="screen-title">Wo bist du?</h1>
      <p className="screen-sub">
        Conquest findet den Ort, in dem du stehst, und teilt dir dann drei Karten aus.
      </p>

      {error ? (
        <div style={{ marginBottom: 16 }}>
          <Notice tone="error">{error}</Notice>
        </div>
      ) : null}

      {unresolvable ? (
        <p className="notice" style={{ marginBottom: 16 }}>
          Conquest wird nur in Städten, Dörfern und Weilern gespielt. Wenn du gerade
          zwischen zwei Orten bist, versuch es noch einmal, sobald du angekommen bist.
        </p>
      ) : null}

      <button className="btn btn--lg btn--block" onClick={() => void detect()} disabled={busy}>
        {busy ? <span className="spin" aria-hidden /> : null}
        {phase === "locating" ? "Wir suchen dich" : phase === "detecting" ? "Wir bestimmen den Ort" : "Meinen Ort finden"}
      </button>

      {manual ? (
        <div style={{ marginTop: 22 }}>
          <PlaceSearch autoFocus onPick={pickSearched} />
          <button
            className="btn btn--ghost btn--block"
            style={{ marginTop: 10 }}
            onClick={() => setManual(false)}
          >
            Doch meinen Standort verwenden
          </button>
        </div>
      ) : (
        <button
          className="btn btn--ghost btn--block"
          style={{ marginTop: 12 }}
          onClick={() => setManual(true)}
        >
          Stattdessen einen Ortsnamen eingeben
        </button>
      )}
    </div>
  );
}
